import React, { useState, useEffect } from 'react';
import { Table, Card, message, Select, Row, Col, Typography, Button, Space } from 'antd';
import { withAuthenticationRequired, useAuth0 } from "@auth0/auth0-react";
import { useSearchParams } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';

const { Option } = Select;
const { Title } = Typography;

interface Producto {
  id: number;
  nombre: string;
  descripcion: string;
  tipo: number;
}

interface LoteDisponible {
  key: string;
  lote: number;
  cantidad: number;
  aplicado_compras: number;
  stock: number;
  aplicado_ventas: number;
  disponible: number;
  calidad_desde: string;
  calidad_hasta: string;
  micronaire_desde: number | null;
  micronaire_hasta: number | null;
  longitud_desde: number | null;
  longitud_hasta: number | null;
  resistencia_desde: number | null;
  resistencia_hasta: number | null;
  sci_desde: number | null;
  sci_hasta: number | null;
  mst_desde: number | null;
  mst_hasta: number | null;
  ml_desde: number | null;
  ml_hasta: number | null;
  ui_desde: number | null;
  ui_hasta: number | null;
  elg_desde: number | null;
  elg_hasta: number | null;
  amt_desde: number | null;
  amt_hasta: number | null;
  rd_desde: number | null;
  rd_hasta: number | null;
  b_desde: number | null;
  b_hasta: number | null;
  cg_desde: string;
  cg_hasta: string;
  t_cnt_desde: number | null;
  t_cnt_hasta: number | null;
  t_area_desde: number | null;
  t_area_hasta: number | null;
  mr_desde: number | null;
  mr_hasta: number | null;
  sfi_desde: number | null;
  sfi_hasta: number | null;
  trid_desde: number | null;
  trid_hasta: number | null;
}

const ListadoDisponible = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [searchParams, setSearchParams] = useSearchParams();
  const [productos, setProductos] = useState<Producto[]>([]);
  const [productoSeleccionado, setProductoSeleccionado] = useState<number | null>(null);
  const [data, setData] = useState<LoteDisponible[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingProductos, setLoadingProductos] = useState<boolean>(true);
  const [shouldFetch, setShouldFetch] = useState<boolean>(false);

  // Cargar productos tipo=2 (productos de desmote)
  useEffect(() => {
    const fetchProductos = async () => {
      setLoadingProductos(true);
      try {
        const token = await getAccessTokenSilently();
        const urlProductos = `${process.env.REACT_APP_BALANZA_URL}/productos`;
        
        const response = await fetch(urlProductos, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error('Error al cargar productos');
        }
        
        const data = await response.json();
        const productosTipo2 = data.filter((producto: Producto) => producto.tipo === 2);
        setProductos(productosTipo2);
        
        // Predeterminar Fibra o usar el primer producto
        const fibraProducto = productosTipo2.find((p: Producto) => 
          p.descripcion?.toUpperCase().includes('FIBRA') && 
          !p.descripcion?.toUpperCase().includes('FIBRILLA')
        );
        
        if (fibraProducto) {
          setProductoSeleccionado(fibraProducto.id);
        } else if (productosTipo2.length > 0) {
          setProductoSeleccionado(productosTipo2[0].id);
        }
      } catch (error) {
        console.error('Error al cargar productos:', error);
        message.error('Error al cargar productos');
        setProductos([]);
      } finally {
        setLoadingProductos(false);
      }
    };
    fetchProductos();
  }, [getAccessTokenSilently]);

  // Cargar datos cuando se presiona el botón buscar
  useEffect(() => {
    const fetchDatos = async () => {
      if (!productoSeleccionado || !user?.id_empresa || !shouldFetch) {
        return;
      }
      
      setLoading(true);
      setShouldFetch(false);
      try {
        const token = await getAccessTokenSilently();
        const url = `${process.env.REACT_APP_ESTADISTICAS_URL}/REPORTES/${user.id_empresa}/${productoSeleccionado}/`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error('Error al cargar datos');
        }
        
        const responseData = await response.json();
        
        // Transformar los datos para la tabla
        const datosTransformados: LoteDisponible[] = responseData.map((item: any) => ({
          key: `lote-${item.lote}`,
          ...item
        }));
        
        setData(datosTransformados);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        message.error('Error al cargar el listado de disponibles');
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    if (productoSeleccionado && user?.id_empresa && shouldFetch) {
      fetchDatos();
    }
  }, [shouldFetch, productoSeleccionado, user?.id_empresa, getAccessTokenSilently]);

  const handleProductoChange = (value: number) => {
    setProductoSeleccionado(value);
  };

  const handleBuscar = () => {
    if (!productoSeleccionado) {
      message.warning('Por favor seleccione un producto');
      return;
    }
    setShouldFetch(true);
  };

  // Calcular totales
  const totales = {
    aplicadoCompras: data.reduce((sum, item) => sum + (item.aplicado_compras || 0), 0),
    stock: data.reduce((sum, item) => sum + (item.stock || 0), 0),
    aplicadoVentas: data.reduce((sum, item) => sum + (item.aplicado_ventas || 0), 0),
    disponible: data.reduce((sum, item) => {
      const disponible = (item.aplicado_compras || 0) + (item.stock || 0) - (item.aplicado_ventas || 0);
      return sum + disponible;
    }, 0),
  };

  // Función auxiliar para renderizar rangos desde/hasta
  const renderRango = (desde: number | string | null, hasta: number | string | null, formatter?: (val: number) => string) => {
    if (desde === null && hasta === null) return '-';
    if (desde === hasta) {
      return formatter ? formatter(desde as number) : String(desde);
    }
    const desdeStr = desde !== null ? (formatter ? formatter(desde as number) : String(desde)) : '-';
    const hastaStr = hasta !== null ? (formatter ? formatter(hasta as number) : String(hasta)) : '-';
    return `${desdeStr} / ${hastaStr}`;
  };

  // Definir las columnas de la tabla
  const columns: ColumnsType<LoteDisponible> = [
    {
      title: 'Lote',
      dataIndex: 'lote',
      key: 'lote',
      width: 80,
      fixed: 'left',
      align: 'center',
    },
    {
      title: 'Cantidad',
      dataIndex: 'cantidad',
      key: 'cantidad',
      width: 100,
      align: 'right',
      render: (value: number) => Intl.NumberFormat('es-AR').format(value),
    },
    {
      title: 'Aplicado Compras',
      dataIndex: 'aplicado_compras',
      key: 'aplicado_compras',
      width: 130,
      align: 'right',
      render: (value: number) => Intl.NumberFormat('es-AR').format(value),
    },
    {
      title: 'Stock',
      dataIndex: 'stock',
      key: 'stock',
      width: 100,
      align: 'right',
      render: (value: number) => Intl.NumberFormat('es-AR').format(value),
    },
    {
      title: 'Aplicado Ventas',
      dataIndex: 'aplicado_ventas',
      key: 'aplicado_ventas',
      width: 130,
      align: 'right',
      render: (value: number) => Intl.NumberFormat('es-AR').format(value),
    },
    {
      title: 'Disponible',
      key: 'disponible',
      width: 100,
      align: 'right',
      render: (_: any, record: LoteDisponible) => {
        const disponible = (record.aplicado_compras || 0) + (record.stock || 0) - (record.aplicado_ventas || 0);
        return Intl.NumberFormat('es-AR').format(disponible);
      },
    },
    {
      title: 'Calidad',
      children: [
        {
          title: 'Desde',
          dataIndex: 'calidad_desde',
          key: 'calidad_desde',
          width: 80,
          align: 'center',
        },
        {
          title: 'Hasta',
          dataIndex: 'calidad_hasta',
          key: 'calidad_hasta',
          width: 80,
          align: 'center',
        },
      ],
    },
    {
      title: 'Micronaire',
      children: [
        {
          title: 'Desde',
          dataIndex: 'micronaire_desde',
          key: 'micronaire_desde',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
        {
          title: 'Hasta',
          dataIndex: 'micronaire_hasta',
          key: 'micronaire_hasta',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
      ],
    },
    {
      title: 'Longitud (UHML)',
      children: [
        {
          title: 'Desde',
          dataIndex: 'longitud_desde',
          key: 'longitud_desde',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
        {
          title: 'Hasta',
          dataIndex: 'longitud_hasta',
          key: 'longitud_hasta',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
      ],
    },
    {
      title: 'Resistencia (STR)',
      children: [
        {
          title: 'Desde',
          dataIndex: 'resistencia_desde',
          key: 'resistencia_desde',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
        {
          title: 'Hasta',
          dataIndex: 'resistencia_hasta',
          key: 'resistencia_hasta',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
      ],
    },
    {
      title: 'SCI',
      children: [
        {
          title: 'Desde',
          dataIndex: 'sci_desde',
          key: 'sci_desde',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
        {
          title: 'Hasta',
          dataIndex: 'sci_hasta',
          key: 'sci_hasta',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
      ],
    },
    {
      title: 'MST',
      children: [
        {
          title: 'Desde',
          dataIndex: 'mst_desde',
          key: 'mst_desde',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
        {
          title: 'Hasta',
          dataIndex: 'mst_hasta',
          key: 'mst_hasta',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
      ],
    },
    {
      title: 'ML',
      children: [
        {
          title: 'Desde',
          dataIndex: 'ml_desde',
          key: 'ml_desde',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
        {
          title: 'Hasta',
          dataIndex: 'ml_hasta',
          key: 'ml_hasta',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
      ],
    },
    {
      title: 'UI',
      children: [
        {
          title: 'Desde',
          dataIndex: 'ui_desde',
          key: 'ui_desde',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
        {
          title: 'Hasta',
          dataIndex: 'ui_hasta',
          key: 'ui_hasta',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
      ],
    },
    {
      title: 'ELG',
      children: [
        {
          title: 'Desde',
          dataIndex: 'elg_desde',
          key: 'elg_desde',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
        {
          title: 'Hasta',
          dataIndex: 'elg_hasta',
          key: 'elg_hasta',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
      ],
    },
    {
      title: 'AMT',
      children: [
        {
          title: 'Desde',
          dataIndex: 'amt_desde',
          key: 'amt_desde',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
        {
          title: 'Hasta',
          dataIndex: 'amt_hasta',
          key: 'amt_hasta',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
      ],
    },
    {
      title: 'RD',
      children: [
        {
          title: 'Desde',
          dataIndex: 'rd_desde',
          key: 'rd_desde',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
        {
          title: 'Hasta',
          dataIndex: 'rd_hasta',
          key: 'rd_hasta',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
      ],
    },
    {
      title: 'B',
      children: [
        {
          title: 'Desde',
          dataIndex: 'b_desde',
          key: 'b_desde',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
        {
          title: 'Hasta',
          dataIndex: 'b_hasta',
          key: 'b_hasta',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
      ],
    },
    {
      title: 'CG',
      children: [
        {
          title: 'Desde',
          dataIndex: 'cg_desde',
          key: 'cg_desde',
          width: 90,
          align: 'center',
        },
        {
          title: 'Hasta',
          dataIndex: 'cg_hasta',
          key: 'cg_hasta',
          width: 90,
          align: 'center',
        },
      ],
    },
    {
      title: 'T_CNT',
      children: [
        {
          title: 'Desde',
          dataIndex: 't_cnt_desde',
          key: 't_cnt_desde',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
        {
          title: 'Hasta',
          dataIndex: 't_cnt_hasta',
          key: 't_cnt_hasta',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
      ],
    },
    {
      title: 'T_AREA',
      children: [
        {
          title: 'Desde',
          dataIndex: 't_area_desde',
          key: 't_area_desde',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
        {
          title: 'Hasta',
          dataIndex: 't_area_hasta',
          key: 't_area_hasta',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
      ],
    },
    {
      title: 'MR',
      children: [
        {
          title: 'Desde',
          dataIndex: 'mr_desde',
          key: 'mr_desde',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
        {
          title: 'Hasta',
          dataIndex: 'mr_hasta',
          key: 'mr_hasta',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
      ],
    },
    {
      title: 'SFI',
      children: [
        {
          title: 'Desde',
          dataIndex: 'sfi_desde',
          key: 'sfi_desde',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
        {
          title: 'Hasta',
          dataIndex: 'sfi_hasta',
          key: 'sfi_hasta',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
      ],
    },
    {
      title: 'TRID',
      children: [
        {
          title: 'Desde',
          dataIndex: 'trid_desde',
          key: 'trid_desde',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
        {
          title: 'Hasta',
          dataIndex: 'trid_hasta',
          key: 'trid_hasta',
          width: 90,
          align: 'right',
          render: (value: number | null) => value !== null ? value.toFixed(2) : '-',
        },
      ],
    },
  ];

  return (
    <>
      <div style={{ 
        background: '#40a9ff', 
        padding: '15px 20px', 
        marginBottom: '20px',
        borderRadius: '4px',
        textAlign: 'center'
      }}>
        <h1 style={{ color: 'white', margin: 0 }}>Listado Disponible por Lote</h1>
      </div>
      
      {/* Selector de Producto */}
      <Row style={{ marginBottom: '20px', marginLeft: 0 }}>
        <Col xs={24} sm={18} md={14} lg={12}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: 'bold',
              color: '#023059'
            }}>
              Producto:
            </label>
            <Space style={{ width: '100%' }}>
              <Select
                value={productoSeleccionado}
                onChange={handleProductoChange}
                placeholder="Seleccione un producto"
                loading={loadingProductos}
                style={{ flex: 1, minWidth: '300px' }}
                showSearch
                filterOption={(input, option) =>
                  (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {productos.map((producto) => (
                  <Option key={producto.id} value={producto.id}>
                    {producto.descripcion || producto.nombre}
                  </Option>
                ))}
              </Select>
              <Button 
                type="primary" 
                onClick={handleBuscar}
                loading={loading}
              >
                Buscar
              </Button>
            </Space>
          </div>
        </Col>
      </Row>

      {/* Tabla de Datos */}
      <Card style={{ margin: '0 auto', width: '95%' }}>
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          scroll={{ x: 3000, y: 600 }}
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} lotes`,
          }}
          bordered
          size="small"
        />
        
        {/* Totales */}
        {data.length > 0 && (
          <div style={{ 
            marginTop: '20px', 
            padding: '15px', 
            background: '#f5f5f5', 
            borderRadius: '4px',
            display: 'flex',
            justifyContent: 'space-around',
            flexWrap: 'wrap',
            gap: '20px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                Aplicación Compras
              </div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#023059' }}>
                {Intl.NumberFormat('es-AR').format(totales.aplicadoCompras)}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                Stock
              </div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#023059' }}>
                {Intl.NumberFormat('es-AR').format(totales.stock)}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                Aplicación Ventas
              </div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#023059' }}>
                {Intl.NumberFormat('es-AR').format(totales.aplicadoVentas)}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                Disponibilidad Total
              </div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#023059' }}>
                {Intl.NumberFormat('es-AR').format(totales.disponible)}
              </div>
            </div>
          </div>
        )}
      </Card>
    </>
  );
};

export default withAuthenticationRequired(ListadoDisponible);

